import { ChatAvatar } from "@modules/chat/components/chatAvatar"
import { useChatPageContext } from "@modules/chat/contexts/chatPageContext"
import { type SearchResultType } from "@modules/chat/hooks/usePaginatedChatSearch"
import { word } from "@modules/core/utils/i18n"
import { LoaderCircle } from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import { Channel } from "stream-chat"
import { getDisplayImage, getDisplayTitle, useChatContext } from "stream-chat-react"
import { Text } from "@/components/ui/baseUi/text"
import { useChatClient } from "@/modules/chat/stores/useChatStore"

export type MessageSearchListProps = {
  loading: boolean
  loadMore: () => void
  loadingMore?: boolean
  searchResults: SearchResultType[] | undefined
  refreshing?: boolean
  refreshList?: () => void
  showResultCount?: boolean
}

export const MessageSearchList: React.FC<MessageSearchListProps> = ({ loading, searchResults, refreshing }) => {
  const { setActiveChannel } = useChatContext()
  const chatClient = useChatClient()
  const [messageChannels, updateMessageChannels] = useState<Channel[]>([])
  const { setJumpToMessage } = useChatPageContext()
  const [activeSearchResult, setActiveSearchResult] = useState<SearchResultType | undefined>()

  useEffect(() => {
    if (searchResults && searchResults.length > 0) {
      const channelIds: string[] = []
      searchResults.forEach((searchResult) => {
        if (
          searchResult.entity === "message" &&
          searchResult.content.channel?.id &&
          !channelIds.includes(searchResult.content.channel.id)
        )
          channelIds.push(searchResult.content.channel.id)
      })

      if (channelIds.length > 0)
        chatClient?.queryChannels({ id: { $in: channelIds } }).then((channels) => {
          updateMessageChannels(channels)
        })
    }
  }, [searchResults])

  const onSelectSearchResult = async (searchResult: SearchResultType | undefined) => {
    setActiveSearchResult(searchResult)

    if (searchResult?.entity === "message" && searchResult.content.channel?.id) {
      const channels = await chatClient?.queryChannels({ cid: searchResult.content.channel.cid })
      if (channels?.length === 1) {
        setActiveChannel(channels[0])
        setJumpToMessage(searchResult.content.id)
      }
    } else if (searchResult?.entity === "channel") {
      setActiveChannel(searchResult.content)
    }
  }

  const getChannelImage = useCallback(
    (searchResult: SearchResultType): string => {
      if (searchResult.entity === "channel") return getDisplayImage(searchResult.content, chatClient?.user) || ""

      const foundChannel = messageChannels.find(
        (messageChannel) => messageChannel.id === searchResult.content.channel?.id,
      )

      if (foundChannel) return getDisplayImage(foundChannel, chatClient?.user) || ""
      else return ""
    },
    [messageChannels, chatClient?.user],
  )

  const getChannelTitle = useCallback(
    (searchResult: SearchResultType) => {
      if (searchResult.entity === "channel") return getDisplayTitle(searchResult.content, chatClient?.user) || ""

      const foundChannel = messageChannels.find(
        (messageChannel) => messageChannel.id === searchResult.content.channel?.id,
      )

      if (foundChannel) return getDisplayTitle(foundChannel, chatClient?.user) || ""
      else return ""
    },
    [messageChannels, chatClient?.user],
  )

  return (
    <div className="str-chat messaging light str-chat-channel-list str-chat__channel-list str-chat__channel-list-react">
      <div className="str-chat__channel-list-messenger str-chat__channel-list-messenger-react">
        <div className="str-chat__channel-list-messenger__main str-chat__channel-list-messenger-react__main">
          {!refreshing &&
            (!searchResults || searchResults.length === 0) &&
            (loading ? (
              <div className="mt-4 flex justify-center">
                <LoaderCircle />
              </div>
            ) : (
              <div className="mt-5 flex justify-center text-center">
                <Text fontStyle="LL">{word("chat.no_result.title")}</Text>
              </div>
            ))}
          {searchResults?.map((searchResult) => (
            <button
              key={searchResult.content.id}
              className={
                "str-chat__channel-preview-messenger str-chat__channel-preview str-chat__channel-preview-messenger" +
                (searchResult.content.id === activeSearchResult?.content.id
                  ? " str-chat__channel-preview--active str-chat__channel-preview-messenger--active"
                  : "")
              }
              onClick={() => {
                onSelectSearchResult(searchResult)
              }}
            >
              <div className="str-chat__channel-preview-messenger--left">
                <ChatAvatar
                  image={getChannelImage(searchResult)}
                  name={getChannelTitle(searchResult)}
                  profileAvatarClassName="size-10"
                />
              </div>
              <div className="str-chat__channel-preview-messenger--right str-chat__channel-preview-end">
                <div className="str-chat__channel-preview-end-first-row">
                  <div className="str-chat__channel-preview-messenger--name">{getChannelTitle(searchResult)}</div>
                </div>

                <div className="str-chat__channel-preview-messenger--last-message">
                  {searchResult.entity === "channel"
                    ? (searchResult.content.lastMessage()?.text ?? word("chat.channel.preview.nothingYet"))
                    : searchResult.content.text}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
